import FileSaver from 'file-saver';
import { formatReportPeriod, fullName } from '../format';
import { hoursFromSeconds, motivationLabel, normalizeOcc, roundMoney, applyPayrollBonus } from './formula';
import Utils from '../components/utils';

const { contractTypes, parttimeTypes } = Utils.options();

export const SHEET_COLUMNS = [
    { key: 'contract', title: 'Тип договора', kind: 'text', width: 120 },
    { key: 'parttime', title: 'Ставка', kind: 'text', width: 90 },
    { key: 'login', title: 'Логин', kind: 'text', width: 80 },
    { key: 'name', title: 'Имя', kind: 'text', width: 240 },
    { key: 'totalTime', title: 'Общее время', kind: 'text', width: 110 },
    { key: 'paidTime', title: 'Время опл.', kind: 'text', width: 110 },
    { key: 'talkTime', title: 'В разговоре', kind: 'text', width: 110 },
    { key: 'occ', title: 'OCC, %', kind: 'number', width: 80 },
    { key: 'hoursPaid', title: 'Часы опл.', kind: 'number', width: 90 },
    { key: 'hoursRecalc', title: 'Перерасчёт часов', kind: 'number', width: 120 },
    { key: 'basePay', title: 'База', kind: 'money', width: 90 },
    { key: 'bonusPercent', title: 'Премия, %', kind: 'number', width: 90 },
    { key: 'bonusPay', title: 'Премия', kind: 'money', width: 90 },
    { key: 'allowance', title: 'Надбавки', kind: 'money', width: 90 },
    { key: 'schemeTotal', title: 'Итого схема', kind: 'money', width: 110 },
    { key: 'normFullHours', title: 'Норма месяца', kind: 'number', width: 110 },
    { key: 'normHours', title: 'Личная норма', kind: 'number', width: 110 },
    { key: 'salaryApplied', title: 'Оклад', kind: 'money', width: 90 },
    { key: 'contractPay', title: 'Контракт', kind: 'money', width: 100 },
    { key: 'toPay', title: 'К выплате', kind: 'money', width: 100 },
    { key: 'version', title: 'Версия мотивации', kind: 'text', width: 160 },
];

export const formatClock = (seconds) => {
    const n = Number(seconds);
    if (!Number.isFinite(n) || n < 0) return '';
    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const s = Math.floor(n % 60);
    const pad = (value) => String(value).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

const contractLabel = (row) => {
    const type = row.calculation?.contractType || row.operator?.contract?.type || row.contractType || row.contract?.type;
    return contractTypes.find((item) => item.id === type)?.name || type || '';
};

const parttimeLabel = (row) => {
    const id = row.calculation?.parttime || row.parttime || row.operator?.contract?.parttime;
    return parttimeTypes.find((item) => item.id === id)?.name || id || '';
};

export const buildSheetRow = (row) => {
    const calc = row.calculation || {};
    const statistic = row.statistic || {};
    const totalTime = statistic.totalTime ?? row.totalTime;
    const paidTime = statistic.paid ?? row.paidTime;
    const talkTime = statistic.talkTime ?? row.talkTime;
    const hoursPaid = calc.hoursPaid != null ? calc.hoursPaid : hoursFromSeconds(paidTime);
    const hoursRecalc = calc.hoursRecalc != null ? calc.hoursRecalc : null;
    const occ = calc.occPercent ?? row.occPercent ?? statistic.occPercent;
    return {
        id: row._id || row.id,
        contract: contractLabel(row),
        parttime: parttimeLabel(row),
        login: row.login || row.operator?.login || '',
        name: fullName(row) || fullName(row.operator) || '',
        totalTime: formatClock(totalTime),
        paidTime: formatClock(paidTime),
        talkTime: formatClock(talkTime),
        occ: occ == null || occ === '' ? null : roundMoney(normalizeOcc(occ)),
        hoursPaid: hoursPaid == null ? null : roundMoney(hoursPaid),
        hoursRecalc: hoursRecalc == null ? null : roundMoney(hoursRecalc),
        basePay: calc.basePay != null ? roundMoney(calc.basePay) : null,
        bonusPercent: calc.bonusPercent != null ? roundMoney(calc.bonusPercent) : null,
        bonusPay: calc.bonusPay != null ? roundMoney(calc.bonusPay) : (row.premia != null ? roundMoney(row.premia) : null),
        allowance: calc.allowance != null ? roundMoney(calc.allowance) : 0,
        schemeTotal: calc.schemeTotal != null ? roundMoney(calc.schemeTotal) : null,
        normFullHours: calc.normFullHours != null ? roundMoney(calc.normFullHours) : null,
        normHours: calc.normHours != null ? roundMoney(calc.normHours) : null,
        salaryApplied: calc.salaryApplied != null ? roundMoney(calc.salaryApplied) : null,
        contractPay: calc.contractPay != null ? roundMoney(calc.contractPay) : null,
        toPay: calc.toPay != null ? roundMoney(calc.toPay) : (row.summa != null ? roundMoney(row.summa) : null),
        payoutKind: calc.payoutKind || (contractLabel(row) === 'Контракт' ? 'contract' : 'scheme'),
        status: row.status,
        version: motivationLabel(row.motivation || row.motivationVersion || calc.motivation),
    };
};

export const applyBonusToSheetRow = (row, patch) => {
    const next = applyPayrollBonus(row, patch);
    return { ...row, ...next };
};

export const buildSheetTotals = (rows) => {
    const sum = (key) => roundMoney(rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0));
    return {
        contract: '',
        parttime: '',
        login: '',
        name: 'Итого',
        totalTime: '',
        paidTime: '',
        talkTime: '',
        occ: null,
        hoursPaid: sum('hoursPaid'),
        hoursRecalc: sum('hoursRecalc'),
        basePay: sum('basePay'),
        bonusPercent: null,
        bonusPay: sum('bonusPay'),
        allowance: sum('allowance'),
        schemeTotal: sum('schemeTotal'),
        normFullHours: null,
        normHours: null,
        salaryApplied: sum('salaryApplied'),
        contractPay: sum('contractPay'),
        toPay: sum('toPay'),
        version: '',
    };
};

const escapeXml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const xmlCell = (column, value) => {
    if (value == null || value === '') {
        return '<Cell></Cell>';
    }
    if (column.kind === 'number' || column.kind === 'money') {
        const n = Number(value);
        if (!Number.isFinite(n)) return `<Cell><Data ss:Type="String">${escapeXml(value)}</Data></Cell>`;
        return `<Cell ss:StyleID="${column.kind === 'money' ? 'Money' : 'Num'}"><Data ss:Type="Number">${n}</Data></Cell>`;
    }
    return `<Cell><Data ss:Type="String">${escapeXml(value)}</Data></Cell>`;
};

export const sheetToExcelXml = (rows, period) => {
    const totals = rows.length ? buildSheetTotals(rows) : null;
    const header = SHEET_COLUMNS.map((column) => (
        `<Cell ss:StyleID="Header"><Data ss:Type="String">${escapeXml(column.title)}</Data></Cell>`
    )).join('');
    const body = rows.map((row) => (
        `<Row>${SHEET_COLUMNS.map((column) => xmlCell(column, row[column.key])).join('')}</Row>`
    )).join('');
    const totalRow = totals
        ? `<Row ss:StyleID="Total">${SHEET_COLUMNS.map((column) => xmlCell(column, totals[column.key])).join('')}</Row>`
        : '';
    const widths = SHEET_COLUMNS.map((column) => `<Column ss:AutoFitWidth="0" ss:Width="${Math.round(column.width * 0.75)}"/>`).join('');
    const title = `Расчёт ЗП ${formatReportPeriod(period)}`;

    return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Styles>
 <Style ss:ID="Header"><Font ss:Bold="1"/><Interior ss:Color="#EEF3F8" ss:Pattern="Solid"/></Style>
 <Style ss:ID="Money"><NumberFormat ss:Format="#,##0.00"/></Style>
 <Style ss:ID="Num"><NumberFormat ss:Format="#,##0.00"/></Style>
 <Style ss:ID="Total"><Font ss:Bold="1"/></Style>
</Styles>
<Worksheet ss:Name="Расчёт">
<Table>${widths}
<Row><Cell ss:StyleID="Header"><Data ss:Type="String">${escapeXml(title)}</Data></Cell></Row>
<Row>${header}</Row>
${body}
${totalRow}
</Table>
</Worksheet>
</Workbook>`;
};

export const downloadSheetExcel = (rows, period) => {
    const xml = sheetToExcelXml(rows, period);
    const blob = new Blob([`\uFEFF${xml}`], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const name = `Расчёт ЗП ${formatReportPeriod(period)}.xls`;
    FileSaver.saveAs(blob, name);
};

export const formatSheetCell = (column, value) => {
    if (value == null || value === '') return '';
    if (column.kind === 'money') {
        return Number(value).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (column.kind === 'number') {
        return Number(value).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return String(value);
};
